// frontend/src/components/SettingsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { ConfirmDialog } from './ConfirmDialog';
import ChangePassword from './ChangePassword';
import EmailReminderRules from './EmailReminderRules';
import LabManagement from './LabManagement';
import TransnetyxManagement from './TransnetyxManagement'; // Import the new component
import AuditTrail from './AuditTrail';
import ConnectedApps from './ConnectedApps';
import { DataService } from '../services/DataService';
import { Capacitor } from '@capacitor/core';
import * as IAPService from '../services/IAPService';

// Sync and Subscription no longer have their own tabs — they are rendered
// inside General and Account respectively. Callers still deep-link to the old
// names (App.js's "Review" button on the failed-sync banner passes 'sync', and
// navigateToSettings() takes an arbitrary section), so those names are aliased
// here rather than fixed up at every call site. Doing it here also means a
// future caller cannot reintroduce the bug by passing a name that no longer
// resolves and silently landing the user on a blank panel.
const SECTION_ALIASES = {
  sync: 'general',
  subscription: 'account',
};
const resolveSection = (section) => SECTION_ALIASES[section] || section || 'general';

const SettingsManagement = ({ initialSection = 'general' }) => {
  const { user, logout, updateUser } = useAuth();
  const toast = useToast();
  const { confirm, confirmDialogProps } = useConfirm();
  const [activeSection, setActiveSection] = useState(resolveSection(initialSection));
  const [weaningRemindersEnabled, setWeaningRemindersEnabled] = useState(user?.weaning_reminders_enabled || false);
  const [weaningDays, setWeaningDays] = useState(user?.weaning_days || 21);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [autoDeleteEmptyCages, setAutoDeleteEmptyCages] = useState(user?.auto_delete_empty_cages || false);  
  const [transnetyxCronActive, setTransnetyxCronActive] = useState(user?.transnetyx_autofetch_cron || false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [currentPasswordForEmailChange, setCurrentPasswordForEmailChange] = useState('');
  const [userNotes, setUserNotes] = useState(user?.notes || '');
  // Settings saves are action results and now go to toasts. `error` survives
  // for one inline case only: the email field's own validation, which belongs
  // next to the field it is about.
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [transnetyxRefreshToken, setTransnetyxRefreshToken] = useState('');

  // New state for account deletion confirmation modal
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [passwordForDeletion, setPasswordForDeletion] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [failedSyncItems, setFailedSyncItems] = useState([]);

  // IAP state (iOS native only)
  const [iapProducts, setIapProducts] = useState([]);
  const [iapLoading, setIapLoading] = useState(false);
  const [iapError, setIapError] = useState('');
  const [purchasingId, setPurchasingId] = useState(null);

  // Load StoreKit products when the subscription section is opened on iOS
  useEffect(() => {
    // Subscription is rendered inside Account now.
    if (activeSection !== 'account' || !Capacitor.isNativePlatform()) return;
    setIapLoading(true);
    setIapError('');
    IAPService.getProducts()
      .then(result => setIapProducts(result.products || result || []))
      .catch(err => setIapError('IAP error: ' + (err?.message || JSON.stringify(err))))
      .finally(() => setIapLoading(false));
  }, [activeSection]);

  const handleIAPPurchase = useCallback(async (productId) => {
    setPurchasingId(productId);
    setIapError('');
    try {
      const result = await IAPService.purchase(productId);
      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/apple/verify-transaction`,
        { signedTransaction: result.signedTransaction }
      );
      if (resp.data.success) {
        // Refresh user object so subscription_tier updates everywhere
        const meResp = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`);
        // useAuth's setUser isn't directly accessible here; reload the page to pick up the new tier
        window.location.reload();
      }
    } catch (err) {
      if (err?.code === 'USER_CANCELLED') {
        // Silent — user cancelled the sheet
      } else {
        setIapError(err?.response?.data?.error || err?.message || 'Purchase failed. Please try again.');
      }
    } finally {
      setPurchasingId(null);
    }
  }, []);

  const handleRestorePurchases = useCallback(async () => {
    setIapLoading(true);
    setIapError('');
    try {
      const entitlements = await IAPService.restorePurchases();
      if (entitlements.length > 0) {
        // Verify the first active entitlement with the backend
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/apple/verify-transaction`,
          { signedTransaction: entitlements[0].signedTransaction }
        );
        window.location.reload();
      } else {
        setIapError('No active subscriptions found to restore.');
      }
    } catch {
      setIapError('Restore failed. Please try again.');
    } finally {
      setIapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setWeaningRemindersEnabled(user.weaning_reminders_enabled || false);
      setAutoDeleteEmptyCages(user.auto_delete_empty_cages || false);      
      setTransnetyxCronActive(user.transnetyx_autofetch_cron || false);
      setNewEmail(user.email || '');
      setUserNotes(user.notes || '');
    }
  }, [user]);

  useEffect(() => {
    setActiveSection(resolveSection(initialSection));
  }, [initialSection]);

  useEffect(() => {
    DataService.getFailedSyncItems().then(setFailedSyncItems).catch(() => {});
  }, []);

  useEffect(() => {
    // Sync is rendered inside General now, so that is the tab whose opening
    // should refresh the list.
    if (activeSection === 'general') {
      DataService.getFailedSyncItems().then(setFailedSyncItems).catch(() => {});
    }
  }, [activeSection]);

  // Handler for updating general settings like weaning reminders and notes
  // C9: ask for a fresh confirmation link. The endpoint answers the same way
  // whether or not the address needs confirming, so there is nothing to branch
  // on here.
  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
        { email: user.email }
      );
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the confirmation email.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleUpdateSetting = async (settingName, value) => {
    setLoading(true);
    try {
      const payload = {
        [settingName]: value
      };
      
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/settings`, payload);
      toast.success(response.data.message);
      // Keep the cached user in step with what the server just accepted, so
      // other screens reading this setting do not go on using the old value.
      updateUser({ [settingName]: value });
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to update ${settingName}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for saving user notes
  const handleUpdateNotes = async (e) => {
    e.preventDefault();
    handleUpdateSetting('notes', userNotes);
  };

  // Handler for changing email address
  const handleEmailChangeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmedEmail = newEmail.trim();
    if (trimmedEmail === user.email) {
      setError('New email cannot be the same as your current email.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/change-email`, {
        newEmail: trimmedEmail,
        currentPassword: currentPasswordForEmailChange
      });
      // One toast, not a message that silently rewrites itself 2 s later —
      // the re-login note is part of the same outcome, so it is said once.
      toast.success(
        `${response.data.message} Please re-login for full changes to take effect.`
      );
      setCurrentPasswordForEmailChange('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change email.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for account deactivation (deletion)
  const handleDeleteAccount = async () => {
    setLoading(true);
    setDeleteError('');

    if (!passwordForDeletion) {
      setDeleteError('Please enter your current password to confirm.');
      setLoading(false);
      return;
    }

    try {
      // Send the current password in the request body for re-authentication on the backend
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/delete`, {
        data: { currentPassword: passwordForDeletion } // Send password in data for DELETE request
      });

      // Log out user after successful deactivation
      setTimeout(() => {
        logout();
      }, 300); // Give user time to read the message

    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to deactivate account.');
      console.error('Account deactivation error:', err);
      setLoading(false);
    }
  };

  const handleTransnetyxSync = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
        const payload = {
            refreshToken: transnetyxRefreshToken,
        };
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/transnetyx/auth`, payload);
        toast.success(response.data.message);
        setTransnetyxRefreshToken(''); // Clear the token after successful submission
    } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to sync with Transnetyx.');
    } finally {
        setLoading(false);
    }
  };

  // Section bodies are extracted so two of them can be composed into another
  // section without duplicating any JSX. See renderSectionContent.
  const renderGeneral = () => (
          <div className="settings-section">
            <h3>General Settings</h3>
            <div className="form-group toggle-group">
                <input
                type="checkbox"
                id="weaningReminders"
                checked={weaningRemindersEnabled}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setWeaningRemindersEnabled(newValue);
                  handleUpdateSetting('weaning_reminders_enabled', newValue);
                }}
                disabled={loading}
              />
              <label htmlFor="weaningReminders">
                Email weaning reminders when litters are {weaningDays} days old
              </label>
            </div>
            {/* Weaning age (C8). This used to be a hard-coded 21 in five places,
                including the label above, which stated it as though it were a
                fact about the app rather than a setting. Labs wean anywhere
                from 18 to 28 days depending on strain and protocol. */}
            <div className="form-group">
              <label htmlFor="weaningDays">Weaning age (days)</label>
              <input
                type="number"
                id="weaningDays"
                min="7"
                max="60"
                step="1"
                value={weaningDays}
                onChange={(e) => setWeaningDays(e.target.value)}
                onBlur={() => {
                  const days = parseInt(weaningDays, 10);
                  if (!Number.isInteger(days) || days < 7 || days > 60) {
                    // Snap back rather than sending a value the server will refuse.
                    setWeaningDays(user?.weaning_days || 21);
                    return;
                  }
                  if (days !== (user?.weaning_days || 21)) {
                    handleUpdateSetting('weaning_days', days);
                  }
                }}
                disabled={loading}
                style={{ maxWidth: '8rem' }}
              />
              <small style={{ display: 'block', color: '#666' }}>
                Used for weaning reminders and the &ldquo;litters due&rdquo; dashboard count. 7&ndash;60.
              </small>
            </div>
            <div className="form-group toggle-group">
                <input
                type="checkbox"
                id="autoDeleteEmptyCages"
                checked={autoDeleteEmptyCages}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setAutoDeleteEmptyCages(newValue);
                  handleUpdateSetting('auto_delete_empty_cages', newValue);
                }}
                disabled={loading}
              />
              <label htmlFor="autoDeleteEmptyCages">Automatically archive cages when they become empty</label>
             </div>
            <form onSubmit={handleUpdateNotes} style={{marginTop: '20px'}}>
              <div className="form-group">
                <label htmlFor="userNotes">Your Notes</label>
                <textarea
                  id="userNotes"
                  value={userNotes}
                  onChange={(e) => setUserNotes(e.target.value)}
                  placeholder="Personal notes about your SqueakBase account or lab."
                  rows="4"
                  style={{width: '100%'}}
                  disabled={loading}
                />
              </div>
              <button type="submit" disabled={loading} className="primary-button">
                {loading ? 'Saving Notes...' : 'Save Notes'}
              </button>
            </form>
          </div>
  );

  
  const renderAccount = () => (
          <div className="settings-section">
            <h3>Account Management</h3>
            <div className="form-group">
              <label>Username:</label>
              <input type="text" value={user?.username || ''} disabled />
            </div>

            {/* C9: an unconfirmed address is the reason paid features stay
                locked after a purchase, so say that plainly here rather than
                leaving the user to guess why their tier never arrived. */}
            {user?.email_verified === false && (
              <div className="error" style={{ marginBottom: '1rem' }}>
                <p style={{ margin: '0 0 0.5rem' }}>
                  Your email address is not confirmed yet. Password resets and
                  reminders will not reach you, and a subscription bought for
                  this address stays on hold until it is confirmed.
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={loading || resendingVerification}
                  style={{ width: 'auto' }}
                >
                  {resendingVerification ? 'Sending…' : 'Resend confirmation email'}
                </button>
              </div>
            )}

            <form onSubmit={handleEmailChangeSubmit}>
              <div className="form-group">
                <label htmlFor="newEmail">Email Address:</label>
                <input
                  type="email"
                  id="newEmail"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="currentPasswordForEmailChange">Current Password (to confirm email change):</label>
                <input
                  type="password"
                  id="currentPasswordForEmailChange"
                  value={currentPasswordForEmailChange}
                  onChange={(e) => setCurrentPasswordForEmailChange(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" disabled={loading} className="primary-button">
                {loading ? 'Updating Email...' : 'Update Email'}
              </button>
            </form>

            <div className="form-group" style={{marginTop: '20px'}}>
              <label>Password:</label>
              <button onClick={() => setShowChangePasswordModal(true)} className="secondary-button">
                Change Password
              </button>
            </div>

            <div className="form-group" style={{marginTop: '20px'}}>
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="auth-link">
                Privacy Policy
              </a>
              {' · '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="auth-link">
                Terms of Service
              </a>
            </div>

            {/* New: Delete Account Section */}
            <div className="form-group" style={{marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px'}}>
                <h4 style={{color: '#dc3545', marginBottom: '10px'}}>Danger Zone</h4>
                <p className="setting-description" style={{color: '#dc3545'}}>
                    Permanently deletes your account and all associated data including mice, cages, litters, and genotypes.
                    This action cannot be undone.
                </p>
                <button
                    onClick={() => setShowDeleteAccountModal(true)}
                    className="secondary-button"
                    style={{backgroundColor: '#dc3545', color: 'white'}}
                >
                    Delete Account
                </button>
            </div>
          </div>
  );

  
  const renderSubscription = () => (
          <div className="settings-section">
            <h3>Subscription Management</h3>
            <p>Your current plan: <strong>{(user.subscription_tier || 'free').charAt(0).toUpperCase() + (user.subscription_tier || 'free').slice(1)}</strong></p>
            {Capacitor.isNativePlatform() ? (
              // ── iOS: native StoreKit IAP flow ──────────────────────────────
              user.subscription_tier !== 'free' ? (
                <p className="setting-description" style={{marginTop: '10px'}}>
                  Your subscription is active. To cancel, go to Settings &gt; Apple ID &gt; Subscriptions on your device.
                </p>
              ) : (
                <>
                  <p>Choose a plan to unlock premium features.</p>
                  {iapError && <p style={{color: '#dc3545', marginTop: '8px'}}>{iapError}</p>}
                  {iapLoading && !purchasingId ? (
                    <p className="setting-description" style={{marginTop: '10px'}}>Loading plans…</p>
                  ) : (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px'}}>
                      {iapProducts.length === 0 && !iapLoading && (
                        <p className="setting-description">No plans available. Please check your connection and try again.</p>
                      )}
                      {iapProducts.map(product => (
                        <div key={product.productId} style={{border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <div>
                              <strong>{product.localizedTitle}</strong>
                              <p className="setting-description" style={{margin: '4px 0 0'}}>{product.localizedDescription}</p>
                            </div>
                            <span style={{fontWeight: 600, fontSize: '1.1em', marginLeft: '16px', whiteSpace: 'nowrap'}}>
                              {product.displayPrice} / month
                            </span>
                          </div>
                          <button
                            type="button"
                            className="primary-button"
                            style={{marginTop: '12px', width: '100%'}}
                            onClick={() => handleIAPPurchase(product.productId)}
                            disabled={!!purchasingId}
                          >
                            {purchasingId === product.productId ? 'Processing…' : 'Subscribe'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className="secondary-button"
                    style={{marginTop: '16px'}}
                    onClick={handleRestorePurchases}
                    disabled={iapLoading || !!purchasingId}
                  >
                    Restore Purchases
                  </button>
                </>
              )
            ) : (
              // ── Web: Lemon Squeezy portal ───────────────────────────────────
              <>
                <p>Manage your subscription, billing details, and view invoices through our secure partner portal.</p>
                {user.subscription_tier === 'free' ? (
                  <button
                    type="button"
                    onClick={() => window.open('https://shop.squeakbase.com', '_blank', 'noopener,noreferrer')}
                    className="primary-button"
                    style={{marginTop: '10px'}}
                  >
                    Go to Subscription Portal
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => window.open('https://shop.squeakbase.com/billing', '_blank', 'noopener,noreferrer')}
                    className="primary-button"
                    style={{marginTop: '10px'}}
                  >
                    Manage Your Subscription
                  </button>
                )}
                <p className="setting-description">You will be redirected to Lemon Squeezy to manage your subscription.</p>
              </>
            )}
            <div className="form-group" style={{marginTop: '20px'}}>
              <a href="https://www.squeakbase.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="auth-link">
                Privacy Policy
              </a>
              {' · '}
              <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" className="auth-link">
                Terms of Service
              </a>
            </div>
          </div>
  );

  // Extracted from the Dismiss button's inline onClick: the confirmation is
  // awaited now, and an inline arrow inside JSX is the wrong place for that.
  const handleDismissFailedSyncItem = async (itemId) => {
    if (!await confirm('This will permanently discard this change. Are you sure?', {
      confirmLabel: 'Discard',
      danger: true
    })) {
      return;
    }
    await DataService.dismissFailedSyncItem(itemId);
    const items = await DataService.getFailedSyncItems();
    setFailedSyncItems(items);
  };

  const renderSync = () => (
          <div className="settings-section">
            <h3>Failed Sync Items</h3>
            {failedSyncItems.length === 0 ? (
              <p style={{ color: '#666' }}>No failed sync items. All changes have been synced successfully.</p>
            ) : (
              <div>
                <p style={{ marginBottom: '1rem', color: '#856404' }}>
                  The following {failedSyncItems.length} change(s) failed to sync after multiple attempts.
                  You can retry them or dismiss them (dismissed items will be permanently lost).
                </p>
                {failedSyncItems.map(item => (
                  <div key={item.id} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.5rem', backgroundColor: '#fff9e6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong>{item.action}</strong> on {item.table || 'unknown'}
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                          Error: {item.lastError}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.15rem' }}>
                          Failed: {new Date(item.failedAt).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button
                          onClick={async () => {
                            await DataService.retryFailedSyncItem(item.id);
                            const items = await DataService.getFailedSyncItems();
                            setFailedSyncItems(items);
                          }}
                          style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        >
                          Retry
                        </button>
                        <button
                          onClick={() => handleDismissFailedSyncItem(item.id)}
                          className="secondary-button"
                          style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'general':
        // Sync lives here rather than in its own tab. It is consulted rarely —
        // only when something failed — and a permanent top-level tab for it
        // crowded the nav enough to make the tabs people DO use harder to scan.
        // The failed-item count still surfaces on the General tab itself, so a
        // problem is visible without opening anything.
        return (
          <>
            {renderGeneral()}
            {renderSync()}
          </>
        );
      case 'account':
        // Subscription is an account concern, and pairing them means a user
        // looking for billing finds it where they look for their account.
        return (
          <>
            {renderAccount()}
            {renderSubscription()}
          </>
        );
      case 'email-reminders':
        if (user.subscription_tier !== 'standard' && user.subscription_tier !== 'lab' && user.subscription_tier !== 'enterprise') {
          return (
            <div className="settings-section">
              <h3>Custom Email Reminders</h3>
              <p>This is a premium feature. Please upgrade to the Standard plan to create custom email reminders.</p>
              {Capacitor.isNativePlatform() ? (
                <p className="setting-description" style={{ marginTop: "20px" }}>Please visit the Subscription tab to upgrade your plan.</p>
              ) : (
                <button
                  onClick={() => window.open('https://shop.squeakbase.com', '_blank', 'noopener,noreferrer')}
                  className="primary-button"
                  style={{ marginTop: "20px" }}
                >
                  Upgrade to Standard
                </button>
              )}
            </div>
          );
        }
        return (
          <div className="settings-section">
            <h3>Custom Email Reminders</h3>
            <p className="setting-description">
              Create custom email reminders based on mouse, cage, or litter conditions.
              These reminders will be checked daily and sent when conditions are met.
            </p>
            <EmailReminderRules />
          </div>
        );
      case 'lab-management':
        return (
          <div className="settings-section">
            <LabManagement />
          </div>
        );
      case 'transnetyx':
        if (user.subscription_tier !== 'standard' && user.subscription_tier !== 'lab' && user.subscription_tier !== 'enterprise') {
          return (
            <div className="settings-section">
              <h3>Transnetyx</h3>
              <p>This is a premium feature. Please upgrade to the Standard plan to use the Transnetyx genotyping integration.</p>
              {Capacitor.isNativePlatform() ? (
                <p className="setting-description" style={{ marginTop: "20px" }}>Please visit the Subscription tab to upgrade your plan.</p>
              ) : (
                <button
                  onClick={() => window.open('https://shop.squeakbase.com', '_blank', 'noopener,noreferrer')}
                  className="primary-button"
                  style={{ marginTop: "20px" }}
                >
                  Upgrade to Standard
                </button>
              )}
            </div>
          );
        }
        return (
            <div className="settings-section">
                <h3>Transnetyx</h3>
                <div className="form-group toggle-group">
                    <input
                    type="checkbox"
                    id="transnetyxCron"
                    checked={transnetyxCronActive}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setTransnetyxCronActive(newValue);
                      handleUpdateSetting('transnetyx_autofetch_cron', newValue);
                    }}
                    disabled={loading}
                  />
                  <label htmlFor="transnetyxCron">Automatically fetch Transnetyx results daily</label>
                </div>
                <p className="setting-description">
                    Enter your Transnetyx Refresh Token to sync your genotyping results completed over the last day automatically (Done once per day at 12pm EST). <br />Don't know where to find your Token? Click <a href=
                    "https://accounts.transnetyx.com/Account/Details">Here</a> and go to "Manage" under API Options and approve access for Squeakbase! Then copy your Refresh Token (expand arrow under approved vendors)
                </p>
                {user?.has_tyx_integration && (
                    <div style={{ padding: '15px', backgroundColor: '#e7f3ff', border: '1px solid #007bff', borderRadius: '8px', marginBottom: '15px' }}>
                        <p style={{ margin: 0, fontWeight: 'bold' }}>Active Integration Found</p>
                        {user.tyx_expires_at && <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                          Access Token expires: {new Date(user.tyx_expires_at).toLocaleString()}
                        </p>}
                        <p style={{ margin: '10px 0 0 0', fontStyle: 'italic' }}>
                            Submitting a new token will replace the existing one.
                        </p>
                    </div>
                )}
                <form onSubmit={handleTransnetyxSync}>
                    <div className="form-group">
                        <label htmlFor="transnetyxToken">Transnetyx Refresh Token:</label>
                        <input
                            type="text"
                            id="transnetyxToken"
                            value={transnetyxRefreshToken}
                            onChange={(e) => setTransnetyxRefreshToken(e.target.value)}
                            placeholder="Enter your Transnetyx Refresh Token"
                            required
                            disabled={loading}
                        />
                         <div className="field-hint">
                            Your token is stored securely and used only to sync your data.
                        </div>
                    </div>
                    <button type="submit" disabled={loading} className="primary-button">
                        {loading ? 'Connecting...' : 'Connect to Transnetyx'}
                    </button>
                </form>
                <div style={{marginTop: '2rem'}}>
                    <TransnetyxManagement />
                </div>
            </div>
        );
      case 'connected-apps':
        return <ConnectedApps />;
      case 'audit':
        return <AuditTrail />;
      default:
        return null;
    }
  };

  return (
    <div className="settings-management main-content">
      {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
      <div className="settings-nav">
        {/* The failed-sync count moved onto this button along with the Sync
            panel. Without it a sync failure would have no presence in the nav
            at all, and the one thing that tab existed to surface would become
            invisible. */}
        <button
          className={activeSection === 'general' ? 'active' : ''}
          onClick={() => setActiveSection('general')}
          style={failedSyncItems.length > 0 ? { color: '#856404' } : {}}
        >
          General {failedSyncItems.length > 0 ? `(${failedSyncItems.length})` : ''}
        </button>
        <button
          className={activeSection === 'account' ? 'active' : ''}
          onClick={() => setActiveSection('account')}
        >
          Account
        </button>
        <button
          className={activeSection === 'email-reminders' ? 'active' : ''}
          onClick={() => setActiveSection('email-reminders')}
        >
          Email Reminders
        </button>
        <button
          className={activeSection === 'lab-management' ? 'active' : ''}
          onClick={() => setActiveSection('lab-management')}
        >
          Lab Management
        </button>
        <button
          className={activeSection === 'transnetyx' ? 'active' : ''}
          onClick={() => setActiveSection('transnetyx')}
        >
          Transnetyx
        </button>
        <button
          className={activeSection === 'connected-apps' ? 'active' : ''}
          onClick={() => setActiveSection('connected-apps')}
        >
          Connected Apps
        </button>
        <button
          className={activeSection === 'audit' ? 'active' : ''}
          onClick={() => setActiveSection('audit')}
        >
          Audit Trail
        </button>
      </div>

      <div className="settings-content">
        {error && <div className="error">{error}</div>}
        {renderSectionContent()}
      </div>

      {showChangePasswordModal && (
        <ChangePassword onClose={() => setShowChangePasswordModal(false)} />
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          zIndex: 1000 // Ensure it's on top
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '30px', 
            borderRadius: '12px', 
            maxWidth: '500px', 
            width: '90%',
            textAlign: 'left' 
          }}>
            <h3 style={{color: '#dc3545', marginBottom: '15px'}}>Delete Account</h3>
            <p style={{marginBottom: '15px'}}>
              Are you absolutely sure? This will permanently delete your account and all associated data —
              mice, cages, litters, genotypes, and settings. <strong>This cannot be undone.</strong>
            </p>
            <p style={{marginBottom: '20px', fontWeight: 'bold'}}>
              Please enter your current password to confirm:
            </p>

            <div className="form-group">
              <label htmlFor="passwordForDeletion">Current Password:</label>
              <input
                type="password"
                id="passwordForDeletion"
                value={passwordForDeletion}
                onChange={(e) => setPasswordForDeletion(e.target.value)}
                required
                disabled={loading}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            
            {deleteError && (
              <div className="error" style={{marginBottom: '15px'}}>{deleteError}</div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button 
                onClick={handleDeleteAccount} 
                disabled={loading}
                className="primary-button" 
                style={{backgroundColor: '#dc3545', flex: 1}}
              >
                {loading ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setPasswordForDeletion('');
                  setDeleteError('');
                }}
                className="secondary-button"
                style={{flex: 1}}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsManagement;
